import {
	Component,
	ElementRef,
	HostListener,
	computed,
	inject,
	signal,
	viewChild,
	ChangeDetectionStrategy,
} from '@angular/core'
import { toObservable, toSignal } from '@angular/core/rxjs-interop'
import { Router } from '@angular/router'
import { MatIconModule } from '@angular/material/icon'
import {
	catchError,
	debounceTime,
	distinctUntilChanged,
	from,
	map,
	of,
	startWith,
	switchMap,
} from 'rxjs'
import {
	BoReference,
	BusinessObjectTypes,
	getBoContentLocationUrl,
	getBoEditorPath,
} from '@lg/shared/for-studio'
import type {
	BranchNameType,
	BusinessObjectTypeType,
	ModuleIdType,
} from '@lg/shared'
import { StudioTrpcService } from '@lg/ng-shared'
import { isMac, shortcutLabel } from '../../util/keyboard-util'

interface GlobalSearchResult {
	breadcrumb: string
	snippetBefore: string
	matchedText: string
	snippetAfter: string
	url: string
	windowName: string
}

interface GlobalSearchGroup {
	key: string
	label: string
	boType: string
	results: GlobalSearchResult[]
}

interface GlobalSearchState {
	isLoading: boolean
	groups: GlobalSearchGroup[]
	matchCount: number
	isTruncated: boolean
	error?: string
}

/** VS Code shortcuts for the search options: Alt+<key>, or Alt+Cmd+<key> on macOS */
const OPTION_SHORTCUT_KEY_CODES = {
	KeyC: 'isCaseSensitive',
	KeyW: 'isWholeWord',
	KeyR: 'isRegex',
} as const

function optionShortcutLabel(key: string) {
	return shortcutLabel(isMac() ? `Alt+Ctrl+${key}` : `Alt+${key}`)
}

const emptyState: GlobalSearchState = {
	isLoading: false,
	groups: [],
	matchCount: 0,
	isTruncated: false,
}

@Component({
	selector: 'lowgile-global-search',
	templateUrl: './global-search.component.html',
	styleUrls: ['./global-search.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [MatIconModule],
})
export class GlobalSearchComponent {
	private trpc = inject(StudioTrpcService)
	private router = inject(Router)
	private input = viewChild<ElementRef<HTMLInputElement>>('input')
	readonly boTypes = [...BusinessObjectTypes].sort()
	readonly optionShortcutLabels = {
		isCaseSensitive: optionShortcutLabel('C'),
		isRegex: optionShortcutLabel('R'),
		isWholeWord: optionShortcutLabel('W'),
	}

	isVisible = signal(false)
	query = signal('')
	isRegex = signal(false)
	isCaseSensitive = signal(false)
	isWholeWord = signal(false)
	isCurrentModuleOnly = signal(false)
	boType = signal<BusinessObjectTypeType | undefined>(undefined)
	currentModuleId = signal<ModuleIdType | undefined>(undefined)
	activeIndex = signal(0)
	collapsedGroupKeys = signal(new Set<string>())

	private searchParams = computed(() => ({
		query: this.isVisible() ? this.query() : '',
		isRegex: this.isRegex(),
		isCaseSensitive: this.isCaseSensitive(),
		isWholeWord: this.isWholeWord(),
		moduleId: this.isCurrentModuleOnly() ? this.currentModuleId() : undefined,
		boType: this.boType(),
	}))

	state = toSignal(
		toObservable(this.searchParams).pipe(
			debounceTime(300),
			distinctUntilChanged(
				(a, b) =>
					a.query === b.query &&
					a.isRegex === b.isRegex &&
					a.isCaseSensitive === b.isCaseSensitive &&
					a.isWholeWord === b.isWholeWord &&
					a.moduleId === b.moduleId &&
					a.boType === b.boType,
			),
			switchMap((params) => {
				if (!params.query) return of(emptyState)
				return this.search(params).pipe(
					startWith({ ...emptyState, isLoading: true }),
				)
			}),
		),
		{ initialValue: emptyState },
	)

	/** Results of all expanded groups, in display order (for keyboard navigation) */
	visibleResults = computed(() => {
		const collapsedGroupKeys = this.collapsedGroupKeys()
		return this.state()
			.groups.filter((group) => !collapsedGroupKeys.has(group.key))
			.flatMap((group) => group.results)
	})
	private visibleResultIndexes = computed(
		() => new Map(this.visibleResults().map((result, idx) => [result, idx])),
	)

	isActive(result: GlobalSearchResult) {
		return this.visibleResultIndexes().get(result) === this.activeIndex()
	}

	activate(result: GlobalSearchResult) {
		const idx = this.visibleResultIndexes().get(result)
		if (idx !== undefined) this.activeIndex.set(idx)
	}

	@HostListener('document:keydown.control.shift.f', ['$event'])
	@HostListener('document:keydown.meta.shift.f', ['$event'])
	onToggleShortcut(event: KeyboardEvent) {
		event.preventDefault()
		if (this.isVisible()) {
			this.hide()
		} else {
			this.show()
		}
	}

	@HostListener('document:keydown.escape')
	onEscape() {
		if (this.isVisible()) this.hide()
	}

	show() {
		this.currentModuleId.set(this.getRouteParams().moduleId)
		this.isVisible.set(true)
		this.activeIndex.set(0)
		setTimeout(() => this.input()?.nativeElement.select())
	}

	hide() {
		this.isVisible.set(false)
	}

	onOverlayClick(event: MouseEvent) {
		if (event.target === event.currentTarget) this.hide()
	}

	onInput(event: Event) {
		this.query.set((event.target as HTMLInputElement).value)
		this.activeIndex.set(0)
	}

	onBoTypeChange(event: Event) {
		const value = (event.target as HTMLSelectElement).value
		this.boType.set((value || undefined) as BusinessObjectTypeType | undefined)
		this.activeIndex.set(0)
	}

	onOptionShortcut(event: KeyboardEvent) {
		if (!event.altKey || event.ctrlKey || event.shiftKey) return
		const option =
			OPTION_SHORTCUT_KEY_CODES[
				event.code as keyof typeof OPTION_SHORTCUT_KEY_CODES
			]
		if (!option) return

		event.preventDefault()
		this[option].set(!this[option]())
		this.activeIndex.set(0)
	}

	onInputKeyDown(event: KeyboardEvent) {
		const results = this.visibleResults()
		if (!results.length) return

		switch (event.key) {
			case 'ArrowDown':
				event.preventDefault()
				this.activeIndex.set((this.activeIndex() + 1) % results.length)
				break
			case 'ArrowUp':
				event.preventDefault()
				this.activeIndex.set(
					(this.activeIndex() - 1 + results.length) % results.length,
				)
				break
			case 'Enter':
				event.preventDefault()
				this.open(results[this.activeIndex()])
				break
		}
	}

	toggleGroup(group: GlobalSearchGroup) {
		const collapsedGroupKeys = new Set(this.collapsedGroupKeys())
		if (!collapsedGroupKeys.delete(group.key)) {
			collapsedGroupKeys.add(group.key)
		}
		this.collapsedGroupKeys.set(collapsedGroupKeys)
		this.activeIndex.set(0)
	}

	open(result: GlobalSearchResult | undefined) {
		if (!result) return
		window.open(result.url, result.windowName)
		this.hide()
	}

	private search(params: {
		query: string
		isRegex: boolean
		isCaseSensitive: boolean
		isWholeWord: boolean
		moduleId: ModuleIdType | undefined
		boType: BusinessObjectTypeType | undefined
	}) {
		const { branchName } = this.getRouteParams()

		return from(
			this.trpc.client.bo.searchBoContent.query({ branchName, ...params }),
		).pipe(
			map((result): GlobalSearchState => {
				const groups = new Map<string, GlobalSearchGroup>()
				for (const match of result.matches) {
					const boRef = new BoReference(match.boRef)
					const windowName = getBoEditorPath(branchName, boRef)
					let group = groups.get(windowName)
					if (!group) {
						group = {
							key: windowName,
							label: `${boRef.moduleId}.${boRef.boId}`,
							boType: boRef.boType,
							results: [],
						}
						groups.set(windowName, group)
					}
					group.results.push({
						breadcrumb: [
							...match.breadcrumb,
							...(match.lineNumber ? [`line ${match.lineNumber}`] : []),
						].join(' › '),
						snippetBefore: match.snippet.before,
						matchedText: match.matchedText,
						snippetAfter: match.snippet.after,
						url: getBoContentLocationUrl(branchName, {
							boRef,
							path: match.path,
						}),
						windowName,
					})
				}
				return {
					isLoading: false,
					groups: [...groups.values()],
					matchCount: result.matches.length,
					isTruncated: result.isTruncated,
				}
			}),
			catchError((err) =>
				of({ ...emptyState, error: err?.message ?? String(err) }),
			),
		)
	}

	private getRouteParams() {
		let route = this.router.routerState.snapshot.root
		while (route.firstChild) route = route.firstChild

		return {
			branchName: (route.params['branchName'] || 'master') as BranchNameType,
			moduleId: route.params['moduleId'] as ModuleIdType | undefined,
		}
	}
}
